import type { Idl, Program } from '@coral-xyz/anchor';
import type { Connection, PublicKey, TransactionInstruction } from '@solana/web3.js';
import {
  MORFI_OPINION_PROGRAM_ID,
  SELL_CAP_PROGRAM,
  SYSTEM_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import {
  derivePositionPda,
  deriveSellCapConfigPda,
  deriveSellCapStatePda,
} from '../pda/derivations.js';
import type { BuildParams } from '../types/build-result.js';
import { invokeMethod } from './common.js';

/** Opens one trader's cost-basis position for one outcome of a capped market. */
export interface InitPositionParams extends BuildParams {
  market: PublicKey;
  outcomeIndex: number;
  /** Trader who will own the position; need not sign. */
  owner: PublicKey;
  /** Rent payer and signer, e.g. the gasless relay fee payer. */
  payer: PublicKey;
}

/**
 * Accounts the sell-cap program adds to `buy`/`sell`; empty for the classic
 * program so its instructions stay byte-identical. The canonical position is
 * always passed — uncapped markets ignore it.
 */
export function sellCapTradeAccounts(
  programId: PublicKey,
  market: PublicKey,
  outcomeIndex: number,
  trader: PublicKey,
): Record<string, PublicKey> {
  if (!SELL_CAP_PROGRAM) return {};
  return {
    sellCapState: deriveSellCapStatePda(market, programId)[0],
    position: derivePositionPda(market, outcomeIndex, trader, programId)[0],
  };
}

/** Accounts the sell-cap program adds to `create_market`; empty for the classic program. */
export function sellCapCreationAccounts(
  programId: PublicKey,
  market: PublicKey,
  marketConfig: PublicKey,
): Record<string, PublicKey> {
  if (!SELL_CAP_PROGRAM) return {};
  return {
    sellCapConfig: deriveSellCapConfigPda(marketConfig, programId)[0],
    sellCapState: deriveSellCapStatePda(market, programId)[0],
  };
}

/** `init_position` instruction; idempotent on chain. Sell-cap program only. */
export function buildInitPositionInstruction(
  program: Program<Idl>,
  params: InitPositionParams,
): Promise<TransactionInstruction> {
  const programId = program.programId;
  return invokeMethod(program, 'initPosition', params.outcomeIndex)
    .accounts({
      payer: params.payer,
      owner: params.owner,
      market: params.market,
      sellCapState: deriveSellCapStatePda(params.market, programId)[0],
      position: derivePositionPda(params.market, params.outcomeIndex, params.owner, programId)[0],
      systemProgram: SYSTEM_PROGRAM_ID,
    })
    .instruction();
}

/**
 * `init_position` when the market is capped (its SellCapState is owned by the
 * program) and the trader has no position yet; otherwise null. A capped market
 * rejects buys without an initialized position (`PositionNotInitialized`), so
 * builders prepend this before a trader's first buy. Always null for the
 * classic program, which performs no read.
 */
export async function buildInitPositionIfNeeded(
  connection: Connection,
  params: InitPositionParams,
): Promise<TransactionInstruction | null> {
  if (!SELL_CAP_PROGRAM) return null;
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const [state, position] = await connection.getMultipleAccountsInfo([
    deriveSellCapStatePda(params.market, programId)[0],
    derivePositionPda(params.market, params.outcomeIndex, params.owner, programId)[0],
  ]);
  if (state?.owner.equals(programId) !== true) return null;
  if (position?.owner.equals(programId) === true) return null;
  return buildInitPositionInstruction(await getProgram(connection, programId), params);
}

/**
 * Base58 addresses of the given presets whose SellCapConfig exists, i.e. whose
 * new markets are sell-capped. Empty for the classic program, which performs
 * no read. Throws when the RPC read fails.
 */
export async function fetchSellCappedMarketConfigs(
  connection: Connection,
  marketConfigs: readonly PublicKey[],
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<Set<string>> {
  const capped = new Set<string>();
  if (!SELL_CAP_PROGRAM) return capped;
  for (let offset = 0; offset < marketConfigs.length; offset += 100) {
    const batch = marketConfigs.slice(offset, offset + 100);
    const infos = await connection.getMultipleAccountsInfo(
      batch.map((config) => deriveSellCapConfigPda(config, programId)[0]),
    );
    batch.forEach((config, index) => {
      if (infos[index]?.owner.equals(programId) === true) capped.add(config.toBase58());
    });
  }
  return capped;
}
